import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "@/api/client";
import { getAdminActivitySystem, getAdminPlaybackSessions } from "@/api/endpoints";
import { useApi } from "@/hooks/useApi";
import { itemArtworkSrc } from "@/lib/artwork";
import { formatDurationMSOrZero, formatEpisodeCodeFromNumbers } from "@/lib/mediaFormat";
import { Sparkline } from "@/components/Sparkline";
import { StatusBadge } from "@/components/StatusBadge";
import type { AdminActivitySystemAttributes, AdminPlaybackSession, Item, PlaybackClientState, PlaybackVariant } from "@/types/api";

const ACTIVE_POLL_INTERVAL_MS = 5_000;
const HISTORY_POLL_INTERVAL_MS = 60_000;
const CHART_WINDOW_MS = 5 * 60_000;
const CHART_MAX_SAMPLES = Math.ceil(CHART_WINDOW_MS / ACTIVE_POLL_INTERVAL_MS) + 4;

interface ActivitySample {
  t: number;
  bandwidthBps: number;
  streamCount: number;
  transcodeCount: number;
  hostLoadPercent: number;
}

export function PlaybackPage() {
  const [deepDiagnostics, setDeepDiagnostics] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [showServerMetrics, setShowServerMetrics] = useState(false);
  const activeSessionsRequest = useApi(
    () => getAdminPlaybackSessions({ include: "item", diagnostics: deepDiagnostics ? "source" : "stream" }),
    [deepDiagnostics],
  );
  const historySessionsRequest = useApi(
    () => getAdminPlaybackSessions({ include: "item", all: true, limit: 50 }),
    [],
  );
  const systemRequest = useApi(() => getAdminActivitySystem(), []);
  const refetchActiveSessions = activeSessionsRequest.refetch;
  const refetchHistorySessions = historySessionsRequest.refetch;
  const refetchSystem = systemRequest.refetch;

  useEffect(() => {
    const timer = window.setInterval(() => {
      void refetchActiveSessions().catch(() => {});
      void refetchSystem().catch(() => {});
    }, ACTIVE_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [refetchActiveSessions, refetchSystem]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void refetchHistorySessions().catch(() => {});
    }, HISTORY_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [refetchHistorySessions]);

  const summary = activeSessionsRequest.data?.meta.summary;
  const activeSessions = useMemo(
    () => [...(activeSessionsRequest.data?.data ?? [])].filter((session) => session.attributes.state !== "stopped").sort(compareSessionsByUpdatedAt),
    [activeSessionsRequest.data?.data],
  );
  const historyAllSessions = useMemo(
    () => [...(historySessionsRequest.data?.data ?? [])].sort(compareSessionsByUpdatedAt),
    [historySessionsRequest.data?.data],
  );
  const historySessions = useMemo(
    () => historyAllSessions.filter((session) => session.attributes.state === "stopped").slice(0, 20),
    [historyAllSessions],
  );
  const userActivity = useMemo(
    () => summarizeUserActivity(historyAllSessions),
    [historyAllSessions],
  );
  const systemMetrics = systemRequest.data?.data.attributes ?? null;
  const activeThroughput = systemMetrics?.playback.throughput_bps
    ?? activeSessions.reduce((total, session) => total + Math.max(0, session.attributes.last_bandwidth_bps), 0);
  const activeTranscodes = systemMetrics?.playback.transcoding_sessions
    ?? activeSessions.filter((session) => isTranscoding(session.attributes)).length;
  const activeStalls = activeSessions.reduce((total, session) => total + Math.max(0, session.attributes.consecutive_stalls), 0);
  const maxUserWatchMS = Math.max(1, ...userActivity.map((activity) => activity.watchedMS));
  const activityError = activeSessionsRequest.error ?? systemRequest.error;

  const samplesRef = useRef<ActivitySample[]>([]);
  const [samples, setSamples] = useState<ActivitySample[]>([]);
  const lastUpdatedKey = activeSessionsRequest.data ?? systemRequest.data;
  useEffect(() => {
    if (!lastUpdatedKey) return;
    const sample: ActivitySample = {
      t: Date.now(),
      bandwidthBps: activeThroughput,
      streamCount: activeSessions.length,
      transcodeCount: activeTranscodes,
      hostLoadPercent: clampPercent(systemMetrics?.load?.normalized_percent ?? 0),
    };
    const next = [...samplesRef.current, sample]
      .filter((s) => s.t >= sample.t - CHART_WINDOW_MS)
      .slice(-CHART_MAX_SAMPLES);
    samplesRef.current = next;
    setSamples(next);
  }, [lastUpdatedKey, activeThroughput, activeSessions.length, activeTranscodes, systemMetrics]);

  const bandwidthSeries = useMemo(
    () => samples.map((s) => ({ t: s.t, v: s.bandwidthBps })),
    [samples],
  );
  const hostLoadSeries = useMemo(
    () => samples.map((s) => ({ t: s.t, v: s.hostLoadPercent })),
    [samples],
  );
  const peakBandwidth = useMemo(
    () => Math.max(1, ...samples.map((s) => s.bandwidthBps)),
    [samples],
  );

  return (
    <div className="admin-page">
      <div className="activity-page-header">
        <div>
          <h1>Activity</h1>
          <p className="settings-copy admin-page-copy">
            Live streams, recent playback, and per-user totals.
          </p>
        </div>
        <span className="badge badge--muted activity-live-badge">
          <span className="activity-live-dot" aria-hidden="true" />
          Live · refreshes every 5s
        </span>
      </div>

      <section className="activity-charts" aria-label="Activity charts">
        <ChartCard
          title="Bandwidth"
          value={formatBitrate(activeThroughput)}
          hint="last 5m"
          accent="rgba(94, 184, 88, 0.95)"
          fillFrom="rgba(94, 184, 88, 0.95)"
          chart={
            <Sparkline
              data={bandwidthSeries}
              windowMs={CHART_WINDOW_MS}
              color="rgba(94, 184, 88, 0.95)"
              fillFrom="rgba(94, 184, 88, 0.6)"
              yMin={0}
              yMax={Math.max(peakBandwidth * 1.1, 1_000_000)}
              formatY={(value) => formatBitrate(value)}
              ariaLabel="Bandwidth over the last 5 minutes"
            />
          }
        />
        <ChartCard
          title="Host Load"
          value={formatLoadPercent(systemMetrics)}
          hint={formatLoadDetail(systemMetrics)}
          accent="rgba(232, 178, 94, 0.95)"
          fillFrom="rgba(232, 178, 94, 0.95)"
          chart={
            <Sparkline
              data={hostLoadSeries}
              windowMs={CHART_WINDOW_MS}
              color="rgba(232, 178, 94, 0.95)"
              fillFrom="rgba(232, 178, 94, 0.45)"
              yMin={0}
              yMax={100}
              formatY={(value) => `${Math.round(value)}%`}
              ariaLabel="Host load percentage over the last 5 minutes"
            />
          }
        />
      </section>

      <section className="activity-stats-strip" aria-label="Server activity summary">
        <StatTile
          label="Active Streams"
          value={activeSessions.length.toLocaleString()}
          hint={summary && (summary.paused > 0 || summary.buffering > 0)
            ? `${summary.paused} paused · ${summary.buffering} buffering`
            : "current viewers"}
        />
        <StatTile
          label="Transcodes"
          value={activeTranscodes.toLocaleString()}
          hint={activeSessions.length > 0
            ? `${activeSessions.length - activeTranscodes} direct play`
            : "none active"}
        />
        {activeStalls > 0 && (
          <StatTile
            label="Stalls"
            value={activeStalls.toLocaleString()}
            hint="streams currently stalled"
            tone="warn"
          />
        )}
        <button
          type="button"
          className="activity-stats-toggle"
          onClick={() => setShowServerMetrics((value) => !value)}
          aria-expanded={showServerMetrics}
        >
          {showServerMetrics ? "Hide server metrics" : "Show server metrics"}
        </button>
      </section>

      {showServerMetrics && (
        <section className="settings-section activity-section">
          <div className="activity-meter-grid">
            <MetricCard
              label="Host Load"
              value={formatLoadPercent(systemMetrics)}
              detail={formatLoadDetail(systemMetrics)}
              percent={clampPercent(systemMetrics?.load?.normalized_percent ?? 0)}
            />
            <MetricCard
              label="I/O Wait"
              value={formatPercent(systemMetrics?.cpu?.iowait_percent)}
              detail={formatIOWaitDetail(systemMetrics)}
              percent={clampPercent(systemMetrics?.cpu?.iowait_percent ?? 0)}
            />
            <MetricCard
              label="KnotFin API"
              value={formatBytes(systemMetrics?.process.heap_alloc_bytes ?? 0)}
              detail={formatProcessDetail(systemMetrics)}
              percent={formatProcessHeapPercent(systemMetrics)}
            />
            <MetricCard
              label="Memory"
              value={formatPercent(systemMetrics?.memory?.used_percent)}
              detail={formatMemoryDetail(systemMetrics)}
              percent={clampPercent(systemMetrics?.memory?.used_percent ?? 0)}
            />
          </div>
          {summary && (
            <div className="diagnostics-count-grid">
              <CountPill label="Paused" value={summary.paused} />
              <CountPill label="Buffering" value={summary.buffering} />
              <CountPill label="Active Stalls" value={activeStalls} />
              <CountPill label="Stale Sessions" value={summary.stale ?? 0} />
              <CountPill label="Recent History" value={summary.stopped} />
            </div>
          )}
          {systemRequest.error && (
            <p className="settings-status settings-status--error">
              {systemRequest.error instanceof ApiError ? systemRequest.error.problem.detail : "Failed to load server metrics"}
            </p>
          )}
        </section>
      )}

      <section className="activity-now-playing" aria-label="Now Playing">
        <div className="activity-section-title">
          <h2>Now Playing</h2>
          {activeSessions.length > 0 && (
            <span className="activity-section-count">{activeSessions.length}</span>
          )}
        </div>

        {activeSessionsRequest.loading && activeSessions.length === 0 && (
          <p className="activity-empty">Loading activity…</p>
        )}
        {activityError && (
          <p className="settings-status settings-status--error">
            {activityError instanceof ApiError ? activityError.problem.detail : "Failed to load activity"}
          </p>
        )}
        {!activeSessionsRequest.loading && !activityError && activeSessions.length === 0 && (
          <p className="activity-empty">No one is streaming right now.</p>
        )}
        {activeSessions.length > 0 && (
          <div className="activity-now-playing-grid">
            {activeSessions.map((session) => (
              <NowPlayingCard key={session.id} session={session} />
            ))}
          </div>
        )}
      </section>

      <div className="activity-split">
        <section className="settings-section activity-section">
          <div className="settings-section-header">
            <h2>Recent Playback</h2>
            <button
              type="button"
              className="diagnostics-chip"
              onClick={() => {
                void refetchHistorySessions().catch(() => {});
              }}
            >
              Refresh
            </button>
          </div>
          {historySessionsRequest.error && (
            <p className="settings-status settings-status--error">
              {historySessionsRequest.error instanceof ApiError ? historySessionsRequest.error.problem.detail : "Failed to load playback history"}
            </p>
          )}
          {historySessions.length === 0 ? (
            <p className="activity-empty">No completed playback sessions yet.</p>
          ) : (
            <ul className="activity-history-list">
              {historySessions.map((session) => (
                <RecentPlaybackItem key={session.id} session={session} />
              ))}
            </ul>
          )}
        </section>

        <section className="settings-section activity-section">
          <div className="settings-section-header">
            <h2>User Playback</h2>
          </div>
          {userActivity.length === 0 ? (
            <p className="activity-empty">No playback history yet.</p>
          ) : (
            <table className="data-table activity-table activity-user-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th className="activity-user-cell-numeric">Sessions</th>
                  <th>Watch Time</th>
                  <th>Latest</th>
                </tr>
              </thead>
              <tbody>
                {userActivity.map((activity) => {
                  const showHandle = activity.username && activity.username.toLowerCase() !== activity.displayName.toLowerCase();
                  return (
                    <tr key={activity.userKey}>
                      <td>
                        <div className="diagnostics-item-cell">
                          <div>{activity.displayName}</div>
                          {showHandle && <div className="diagnostics-subtext">@{activity.username}</div>}
                        </div>
                      </td>
                      <td className="activity-user-cell-numeric">{activity.sessionCount.toLocaleString()}</td>
                      <td>
                        <div className="activity-progress-cell">
                          <div>{formatDurationMSOrZero(activity.watchedMS)}</div>
                          <div className="activity-progress-track" aria-hidden="true">
                            <span style={{ width: `${clampPercent((activity.watchedMS / maxUserWatchMS) * 100)}%` }} />
                          </div>
                        </div>
                      </td>
                      <td>{formatDateTimeLong(activity.latestAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <section className="settings-section activity-section">
        <div className="settings-section-header">
          <h2>Stream Diagnostics</h2>
          <div className="activity-diagnostics-controls">
            <button
              type="button"
              className={`diagnostics-chip ${showDiagnostics ? "diagnostics-chip--active" : ""}`}
              onClick={() => setShowDiagnostics((value) => !value)}
              aria-expanded={showDiagnostics}
            >
              {showDiagnostics ? "Hide diagnostics" : "Show diagnostics"}
            </button>
            {showDiagnostics && (
              <button
                type="button"
                className={`diagnostics-chip ${deepDiagnostics ? "diagnostics-chip--active" : ""}`}
                onClick={() => setDeepDiagnostics((value) => !value)}
              >
                {deepDiagnostics ? "Source Probes On" : "Source Probes Off"}
              </button>
            )}
          </div>
        </div>
        {showDiagnostics && (
          <>
            <p className="settings-note">Live stream state refreshes automatically. Source probes run only when enabled.</p>

            {activeSessionsRequest.loading && <p>Loading sessions...</p>}
            {activeSessionsRequest.error && (
              <p className="settings-status settings-status--error">
                {activeSessionsRequest.error instanceof ApiError ? activeSessionsRequest.error.problem.detail : "Failed to load playback diagnostics"}
              </p>
            )}
            {!activeSessionsRequest.loading && !activeSessionsRequest.error && activeSessions.length === 0 && (
              <p>No visible playback sessions.</p>
            )}
            {activeSessions.length > 0 && (
              <table className="data-table diagnostics-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>User</th>
                    <th>Status</th>
                    <th>Streaming</th>
                    <th>Client</th>
                    <th>Source</th>
                    <th>HLS</th>
                  </tr>
                </thead>
                <tbody>
                  {activeSessions.map((session) => (
                    <PlaybackRow key={session.id} session={session} />
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function ChartCard({
  title,
  value,
  hint,
  accent,
  fillFrom,
  chart,
  legend,
}: {
  title: string;
  value: string;
  hint: string;
  accent: string;
  fillFrom?: string;
  chart: React.ReactNode;
  legend?: React.ReactNode;
}) {
  return (
    <div className="activity-chart-card" style={{ ["--activity-chart-accent" as string]: accent, ["--activity-chart-fill" as string]: fillFrom ?? accent }}>
      <div className="activity-chart-header">
        <div className="activity-chart-meta">
          <div className="activity-chart-title">{title}</div>
          <div className="activity-chart-hint">{hint}</div>
        </div>
        <div className="activity-chart-value">{value}</div>
      </div>
      <div className="activity-chart-body">{chart}</div>
      {legend && <div className="activity-chart-footer">{legend}</div>}
    </div>
  );
}

function StatTile({ label, value, hint, tone }: { label: string; value: string; hint: string; tone?: "warn" }) {
  return (
    <div className={`activity-stat-tile${tone === "warn" ? " activity-stat-tile--warn" : ""}`}>
      <div className="activity-stat-label">{label}</div>
      <div className="activity-stat-value">{value}</div>
      <div className="activity-stat-hint">{hint}</div>
    </div>
  );
}

type UserActivitySummary = {
  userKey: string;
  displayName: string;
  username: string;
  sessionCount: number;
  watchedMS: number;
  latestAt: string;
};

function MetricCard({
  label,
  value,
  detail,
  percent,
}: {
  label: string;
  value: string;
  detail: string;
  percent: number;
}) {
  return (
    <div className="activity-meter-card">
      <div className="activity-meter-header">
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
      <div className="activity-meter-track" aria-hidden="true">
        <span style={{ width: `${clampPercent(percent)}%` }} />
      </div>
      <div className="diagnostics-subtext">{detail}</div>
    </div>
  );
}

function CountPill({ label, value }: { label: string; value: number }) {
  return (
    <div className="diagnostics-count-pill">
      <span>{label}</span>
      <strong>{value.toLocaleString()}</strong>
    </div>
  );
}

function NowPlayingCard({ session }: { session: AdminPlaybackSession }) {
  const attrs = session.attributes;
  const item = embeddedItem(session);
  const itemId = item?.id ?? session.relationships.item.data.id;
  const progress = playbackProgress(attrs.position_ms, attrs.duration_ms ?? 0);
  const streamMode = formatDeliveryMode(attrs);
  const quality = formatVariantSummary(attrs.variants, attrs.active_variant_name) ?? formatBitrate(attrs.target_video_bitrate);
  const transcodeLabel = isTranscoding(attrs) ? attrs.transcode_state.replaceAll("_", " ") : "Direct play";
  const bufferedAheadMS = attrs.client_state?.buffered_ahead_ms ?? 0;
  const stateClass = `activity-card-state activity-card-state--${attrs.state}`;
  const healthClass = `activity-card-health activity-card-health--${attrs.playback_health}`;
  const title = formatStreamTitle(item, attrs);
  const subtitle = formatStreamSubtitle(item, attrs);
  const userLabel = attrs.user_display_name ?? attrs.username ?? session.relationships.user.data.id;
  const userHandle = attrs.username && attrs.username !== userLabel ? `@${attrs.username}` : null;
  const backdropSrc = item ? itemArtworkSrc(item, "backdrop", { width: 960, format: "webp" }) : null;
  const posterSrc = item ? itemArtworkSrc(item, "poster", { width: 200, format: "webp" }) : null;

  return (
    <article className="activity-card">
      {backdropSrc && (
        <div
          className="activity-card-backdrop"
          style={{ backgroundImage: `url(${JSON.stringify(backdropSrc)})` }}
          aria-hidden="true"
        />
      )}
      <div className="activity-card-body">
        {posterSrc ? (
          <Link to={`/items/${itemId}`} className="activity-card-poster">
            <img src={posterSrc} alt="" loading="lazy" />
          </Link>
        ) : (
          <div className="activity-card-poster activity-card-poster--placeholder" aria-hidden="true" />
        )}
        <div className="activity-card-main">
          <div className="activity-card-meta">
            <span className={stateClass}>{formatStateLabel(attrs.state)}</span>
            <span className={healthClass}>{formatPlaybackHealthLabel(attrs.playback_health)}</span>
            {attrs.client_family && <span className="activity-card-chip">{formatClientFamily(attrs.client_family)}</span>}
            {streamMode && <span className="activity-card-chip">{streamMode}</span>}
          </div>
          <Link to={`/items/${itemId}`} className="activity-card-title">{title}</Link>
          {subtitle && <div className="activity-card-subtitle">{subtitle}</div>}
          <div className="activity-card-user">
            <span className="activity-card-user-name">{userLabel}</span>
            {userHandle && <span className="activity-card-user-handle">{userHandle}</span>}
          </div>
          <div className="activity-card-timeline">
            <div className="activity-card-timeline-bar" aria-hidden="true">
              <span style={{ width: `${progress}%` }} />
            </div>
            <div className="activity-card-timeline-labels">
              <span>{formatClock(attrs.position_ms)}</span>
              <span>{attrs.duration_ms ? formatClock(attrs.duration_ms) : "—"}</span>
            </div>
          </div>
          <div className="activity-card-stats">
            <div className="activity-card-stat">
              <div className="activity-card-stat-label">Quality</div>
              <div className="activity-card-stat-value">{quality}</div>
            </div>
            <div className="activity-card-stat">
              <div className="activity-card-stat-label">Pipeline</div>
              <div className="activity-card-stat-value">{transcodeLabel}</div>
            </div>
            <div className="activity-card-stat">
              <div className="activity-card-stat-label">Throughput</div>
              <div className="activity-card-stat-value">
                {attrs.last_bandwidth_bps > 0 ? formatBitrate(attrs.last_bandwidth_bps) : "—"}
              </div>
            </div>
            <div className="activity-card-stat">
              <div className="activity-card-stat-label">Buffer</div>
              <div className="activity-card-stat-value">
                {attrs.client_state ? formatClock(bufferedAheadMS) : "—"}
              </div>
            </div>
          </div>
          {(attrs.stall_count > 0 || attrs.adaptation_count > 0) && (
            <div className="activity-card-footnote">{formatStallSummary(attrs.stall_count, attrs.adaptation_count)}</div>
          )}
        </div>
      </div>
    </article>
  );
}

function RecentPlaybackItem({ session }: { session: AdminPlaybackSession }) {
  const attrs = session.attributes;
  const item = embeddedItem(session);
  const itemId = item?.id ?? session.relationships.item.data.id;
  const title = formatStreamTitle(item, attrs);
  const subtitle = formatStreamSubtitle(item, attrs);
  const posterSrc = item ? itemArtworkSrc(item, "poster", { width: 120, format: "webp" }) : null;
  const userLabel = attrs.user_display_name ?? attrs.username ?? session.relationships.user.data.id;

  return (
    <li className="activity-history-item">
      {posterSrc ? (
        <Link to={`/items/${itemId}`} className="activity-history-poster">
          <img src={posterSrc} alt="" loading="lazy" />
        </Link>
      ) : (
        <div className="activity-history-poster activity-history-poster--placeholder" aria-hidden="true" />
      )}
      <div className="activity-history-info">
        <Link to={`/items/${itemId}`} className="activity-history-title">{title}</Link>
        {subtitle && <div className="activity-history-subtitle">{subtitle}</div>}
        <div className="activity-history-meta">
          <span>{userLabel}</span>
          <span aria-hidden="true">·</span>
          <span>{formatDurationMSOrZero(attrs.position_ms)} watched</span>
          <span aria-hidden="true">·</span>
          <span>{formatDateTimeLong(attrs.updated_at)}</span>
        </div>
      </div>
    </li>
  );
}

function PlaybackRow({ session }: { session: AdminPlaybackSession }) {
  const attrs = session.attributes;
  const item = embeddedItem(session);
  const source = attrs.source_media;
  const stream = attrs.stream_diagnostics;
  const decision = attrs.delivery_diagnostics;

  return (
    <tr>
      <td>
        <div className="diagnostics-item-cell">
          <Link className="inline-link" to={`/items/${item?.id ?? session.relationships.item.data.id}`}>
            {item?.attributes.title ?? attrs.item_title ?? session.relationships.item.data.id}
          </Link>
          <div className="diagnostics-subtext">
            {[
              attrs.item_type ?? item?.attributes.item_type ?? null,
              item?.attributes.series_title ?? null,
              item?.attributes.season_title ?? null,
            ].filter(Boolean).join(" • ") || "Playback item"}
          </div>
        </div>
      </td>
      <td>
        <div className="diagnostics-item-cell">
          <div>{attrs.user_display_name ?? attrs.username ?? session.relationships.user.data.id}</div>
          <div className="diagnostics-subtext">{attrs.username ?? session.relationships.user.data.id}</div>
        </div>
      </td>
      <td>
        <div className="playback-diag-stack">
          <StatusBadge status={attrs.state} />
          <span className="badge badge--muted">{formatPlaybackHealthLabel(attrs.playback_health)}</span>
          <div className="diagnostics-subtext">
            {formatClock(attrs.position_ms)} watched
          </div>
        </div>
      </td>
      <td>
        <div className="playback-diag-stack">
          <div>{formatDeliveryMode(attrs)}</div>
          <div className="diagnostics-subtext">
            Profile {attrs.client_profile_id} • subtitles {formatSubtitleDelivery(attrs.subtitle_delivery)}
          </div>
          <div className="diagnostics-subtext">
            Current quality {formatVariantSummary(attrs.variants, attrs.active_variant_name) ?? formatBitrate(attrs.target_video_bitrate)}
          </div>
          <div className="diagnostics-subtext">
            {attrs.last_bandwidth_bps > 0 ? `Latest bandwidth sample ${formatBitrate(attrs.last_bandwidth_bps)}` : "No bandwidth sample yet"}
          </div>
          <div className="diagnostics-subtext">
            Starts after {attrs.initial_buffer_seconds}s buffered • {attrs.segment_duration_seconds}s segments
          </div>
          <div className="diagnostics-subtext">
            {formatStallSummary(attrs.stall_count, attrs.adaptation_count)}
          </div>
          {decision && (
            <div className="diagnostics-subtext">
              Checks {formatDecisionChecks(decision)}
            </div>
          )}
          {decision?.decision_summary && (
            <div className="diagnostics-subtext">
              Plan {decision.decision_summary}
            </div>
          )}
          {attrs.delivery_reason_codes.length > 0 && (
            <div className="diagnostics-subtext">
              Why {attrs.delivery_reason_codes.map(formatReasonCode).join(", ")}
            </div>
          )}
          {decision?.decision_steps && decision.decision_steps.length > 0 && (
            <ol className="diagnostics-step-list">
              {decision.decision_steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          )}
        </div>
      </td>
      <td>
        <div className="playback-diag-stack">
          {attrs.client_state ? (
            <>
              <div>{formatClientReason(attrs.client_state)}</div>
              <div className="diagnostics-subtext">
                {formatClientFlags(attrs.client_state)}
              </div>
              <div className="diagnostics-subtext">
                Ready {attrs.client_state.ready_state} • buffered ahead {formatClock(attrs.client_state.buffered_ahead_ms)}
              </div>
              <div className="diagnostics-subtext">
                {attrs.client_state.autoplay_blocked ? "Autoplay blocked" : "Autoplay not flagged"} • {formatDateTime(attrs.client_state.captured_at)}
              </div>
              {attrs.client_state.error && <div className="diagnostics-subtext">{attrs.client_state.error}</div>}
            </>
          ) : (
            <div className="diagnostics-subtext">No client snapshot yet</div>
          )}
        </div>
      </td>
      <td>
        <div className="playback-diag-stack">
          {source ? (
            <>
              <div>
                {[source.video_codec?.toUpperCase(), formatResolution(source.width, source.height), formatFrameRate(source.frame_rate)]
                  .filter(Boolean)
                  .join(" • ")}
              </div>
              <div className="diagnostics-subtext">
                {[source.audio_codec?.toUpperCase(), formatChannels(source.audio_channels), source.container]
                  .filter(Boolean)
                  .join(" • ")}
              </div>
              {decision?.deliverable_sidecar_subtitles && decision.deliverable_sidecar_subtitles.length > 0 && (
                <div className="diagnostics-subtext">
                  Sidecar subtitles {decision.deliverable_sidecar_subtitles.join(", ").toUpperCase()}
                </div>
              )}
              <div className="diagnostics-subtext">
                {source.bitrate_bps > 0 ? `${formatBitrate(source.bitrate_bps)} source` : "Source bitrate unavailable"}
              </div>
            </>
	          ) : (
	            <div className="diagnostics-subtext">
	              {decision ? "Source probe incomplete" : "Enable source probes for media details"}
	            </div>
	          )}
	        </div>
      </td>
      <td>
        <div className="playback-diag-stack">
          {stream ? (
            <>
              <div>{stream.playlist_ready ? "Playlist ready" : "Playlist pending"}</div>
              {stream.acceleration && (
                <div className="diagnostics-subtext">
                  Accel {formatAccelerationLabel(stream.acceleration)}
                </div>
              )}
              {stream.log_summary && (
                <>
                  <div className="diagnostics-subtext">
                    Engine {formatFFmpegRuntime(stream.log_summary.runtime, stream.log_summary.version)}
                  </div>
                  {(stream.log_summary.last_speed || stream.log_summary.last_time) && (
                    <div className="diagnostics-subtext">
                      Encode {[
                        stream.log_summary.last_speed ? `${stream.log_summary.last_speed} speed` : null,
                        stream.log_summary.last_time ? `at ${stream.log_summary.last_time}` : null,
                      ].filter(Boolean).join(" • ")}
                    </div>
                  )}
                  {typeof stream.log_summary.dropped_frames === "number" && stream.log_summary.dropped_frames > 0 && (
                    <div className="diagnostics-subtext">
                      Dropped {formatDroppedFrames(stream.log_summary)}
                    </div>
                  )}
                  <div className="diagnostics-subtext">
                    Log {stream.log_summary.error_count} errors • {stream.log_summary.warning_count} warnings
                  </div>
                  {stream.log_summary.recent_errors?.map((line) => (
                    <div key={`error-${line}`} className="diagnostics-log-line diagnostics-log-line--error">{line}</div>
                  ))}
                  {stream.log_summary.recent_warnings?.map((line) => (
                    <div key={`warning-${line}`} className="diagnostics-log-line diagnostics-log-line--warning">{line}</div>
                  ))}
                </>
              )}
              {stream.acceleration?.fallback_reason && (
                <div className="diagnostics-subtext">
                  Fallback {stream.acceleration.fallback_reason.replaceAll("_", " ")}
                </div>
              )}
              <div className="diagnostics-subtext">{stream.segment_count} segments written</div>
              <div className="diagnostics-subtext">
                {stream.last_segment_at ? `Last segment ${formatDateTime(stream.last_segment_at)}` : "No segment written yet"}
              </div>
              {stream.last_error && <div className="diagnostics-subtext">{stream.last_error}</div>}
              {stream.log_tail && (
                <details className="playback-diag-log-details">
                  <summary>Raw FFmpeg log tail</summary>
                  <pre className="playback-diag-log">{stream.log_tail}</pre>
                </details>
              )}
            </>
          ) : (
            <div className="diagnostics-subtext">No live HLS process</div>
          )}
        </div>
      </td>
    </tr>
  );
}

function embeddedItem(session: AdminPlaybackSession): Item | null {
  const item = session.relationships.item.data;
  if (!item || !("attributes" in item)) {
    return null;
  }
  return item as Item;
}

function formatBitrate(value: number) {
  if (!value || value <= 0) return "0 Mbps";
  return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 1 : 2)} Mbps`;
}

function formatPercent(value?: number | null) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "Unavailable";
  }
  return `${value.toFixed(0)}%`;
}

function formatLoadPercent(metrics: AdminActivitySystemAttributes | null) {
  return formatPercent(metrics?.load?.normalized_percent);
}

function formatIOWaitDetail(metrics: AdminActivitySystemAttributes | null) {
  if (!metrics?.cpu) {
    return "I/O wait unavailable";
  }
  return `${metrics.cpu.usage_percent.toFixed(0)}% host CPU busy`;
}

function formatProcessDetail(metrics: AdminActivitySystemAttributes | null) {
  if (!metrics?.process) {
    return "Process unavailable";
  }
  return `${metrics.process.goroutines.toLocaleString()} goroutines`;
}

function formatProcessHeapPercent(metrics: AdminActivitySystemAttributes | null) {
  if (!metrics?.process || metrics.process.heap_sys_bytes <= 0) {
    return 0;
  }
  return clampPercent((metrics.process.heap_alloc_bytes / metrics.process.heap_sys_bytes) * 100);
}

function formatLoadDetail(metrics: AdminActivitySystemAttributes | null) {
  if (!metrics?.load) {
    return "Load unavailable";
  }
  return `${metrics.load.one.toFixed(2)} / ${metrics.load.five.toFixed(2)} / ${metrics.load.fifteen.toFixed(2)} across ${metrics.load.cpu_count} cores`;
}

function formatMemoryDetail(metrics: AdminActivitySystemAttributes | null) {
  if (!metrics?.memory) {
    return "Memory unavailable";
  }
  const used = Math.max(0, metrics.memory.total_bytes - metrics.memory.available_bytes);
  return `${formatBytes(used)} used of ${formatBytes(metrics.memory.total_bytes)}`;
}

function formatBytes(value: number) {
  if (!value || value <= 0) {
    return "0 B";
  }
  const units = ["B", "KB", "MB", "GB", "TB"];
  let amount = value;
  let unitIndex = 0;
  while (amount >= 1024 && unitIndex < units.length - 1) {
    amount /= 1024;
    unitIndex += 1;
  }
  return `${amount.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatReasonCode(value: string) {
  return value.replaceAll("_", " ");
}

function formatSubtitleDelivery(value: AdminPlaybackSession["attributes"]["subtitle_delivery"]) {
  switch (value) {
    case "sidecar_text":
      return "sidecar text";
    case "burn_in_required":
      return "burn-in required";
    case "unsupported":
      return "unsupported";
    case "none":
    default:
      return "none";
  }
}

function formatDecisionChecks(decision: NonNullable<AdminPlaybackSession["attributes"]["delivery_diagnostics"]>) {
  const checks = [
    `video ${decision.video_copy_possible ? "copy" : decision.video_codec_supported ? "transcode" : "unsupported"}`,
    `audio ${decision.audio_copy_possible ? "copy" : decision.audio_codec_supported ? "transcode" : "unsupported"}`,
    decision.container_direct_playable ? "container direct" : "container remux",
  ];
  if (!decision.source_bitrate_within_budget) {
    checks.push("bitrate over budget");
  }
  if (!decision.source_resolution_within_limits) {
    checks.push("resolution over limit");
  }
  if (!decision.source_frame_rate_within_limits) {
    checks.push("fps over limit");
  }
  return checks.join(" • ");
}

function formatAccelerationLabel(acceleration: NonNullable<AdminPlaybackSession["attributes"]["stream_diagnostics"]>["acceleration"]) {
  if (!acceleration) {
    return "unknown";
  }
  const parts = [acceleration.selected];
  if (acceleration.encoder) {
    parts.push(acceleration.encoder);
  }
  return parts.join(" • ");
}

function formatFFmpegRuntime(runtime?: string | null, version?: string | null) {
  const label = runtime === "jellyfin-ffmpeg" ? "Jellyfin FFmpeg" : "FFmpeg";
  if (!version) {
    return label;
  }
  const [shortVersion] = version.split(/\s+/);
  return `${label} ${shortVersion}`;
}

function formatDroppedFrames(summary: NonNullable<NonNullable<AdminPlaybackSession["attributes"]["stream_diagnostics"]>["log_summary"]>) {
  const parts = [`${summary.dropped_frames?.toLocaleString() ?? 0} frames`];
  if (summary.last_time) {
    parts.push(`over ${summary.last_time}`);
  }
  if (typeof summary.dropped_frames_per_minute === "number" && summary.dropped_frames_per_minute > 0) {
    parts.push(`${summary.dropped_frames_per_minute.toFixed(1)} per minute`);
  }
  return parts.join(" • ");
}

function formatStreamTitle(item: Item | null, attrs: AdminPlaybackSession["attributes"]): string {
  if (item?.attributes.item_type === "episode" && item.attributes.series_title) {
    return item.attributes.series_title;
  }
  return item?.attributes.title ?? attrs.item_title ?? "Unknown title";
}

function formatStreamSubtitle(item: Item | null, attrs: AdminPlaybackSession["attributes"]): string | null {
  if (!item) {
    return attrs.item_type ?? null;
  }
  const a = item.attributes;
  if (a.item_type === "episode") {
    const code = formatEpisodeCodeFromNumbers(a.season_number, a.episode_number);
    const episodeTitle = a.episode_title || a.title;
    const parts: string[] = [];
    if (a.season_title) parts.push(a.season_title);
    else if (a.season_number != null) parts.push(`Season ${a.season_number}`);
    if (code) parts.push(code);
    if (episodeTitle && episodeTitle !== a.series_title) parts.push(episodeTitle);
    return parts.join(" · ") || null;
  }
  if (a.item_type === "season") {
    return a.series_title ?? null;
  }
  if (a.year) {
    return String(a.year);
  }
  return null;
}

function formatStateLabel(state: AdminPlaybackSession["attributes"]["state"]): string {
  switch (state) {
    case "active": return "Playing";
    case "paused": return "Paused";
    case "buffering": return "Buffering";
    case "stopped": return "Stopped";
    default: return state;
  }
}

function formatClientFamily(family: string): string {
  switch (family.toLowerCase()) {
    case "web": return "Web";
    case "roku": return "Roku";
    case "ios": return "iOS";
    case "android": return "Android";
    case "tvos": return "tvOS";
    default: return family.charAt(0).toUpperCase() + family.slice(1);
  }
}

function formatPlaybackHealthLabel(health: AdminPlaybackSession["attributes"]["playback_health"]) {
  switch (health) {
    case "critical":
      return "Buffering now";
    case "degraded":
      return "Watching connection";
    case "stable":
      return "Playback steady";
    default:
      return health;
  }
}

function formatDeliveryMode(attrs: AdminPlaybackSession["attributes"]) {
  if (attrs.delivery_type === "direct_play") {
    return "Direct play";
  }
  if (attrs.delivery_type === "remux") {
    return attrs.transcode_state === "audio_transcode" ? "Direct stream with audio conversion" : "Direct stream";
  }
  return attrs.conservative_streaming ? "Adaptive stream with conservative startup" : "Adaptive stream";
}

function formatClientReason(state: PlaybackClientState) {
  return state.reason
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatClientFlags(state: PlaybackClientState) {
  const flags = [
    state.playing ? "playing" : null,
    state.paused ? "paused" : null,
    state.buffering_visible ? "buffering UI" : null,
    state.awaiting_playback_start ? "awaiting start" : null,
    state.ready_to_start ? "ready to start" : null,
  ].filter(Boolean);
  return flags.join(" • ") || "No client flags";
}

function formatVariantSummary(variants: PlaybackVariant[], name: string) {
  const variant = variants.find((candidate) => candidate.name === name) ?? null;
  if (!variant) {
    return name || null;
  }
  const parts = [formatVariantResolution(variant), formatBitrate(variant.bandwidth)].filter(Boolean);
  return parts.length > 0 ? parts.join(" at ") : name;
}

function formatVariantResolution(variant: PlaybackVariant) {
  if (variant.height > 0) {
    return `${variant.height}p`;
  }
  if (variant.width > 0 && variant.height > 0) {
    return `${variant.width}x${variant.height}`;
  }
  return "";
}

function formatStallSummary(stalls: number, adaptations: number) {
  const stallLabel = stalls === 1 ? "1 stall" : `${stalls} stalls`;
  const adaptationLabel = adaptations === 1 ? "1 quality change" : `${adaptations} quality changes`;
  return `${stallLabel} • ${adaptationLabel}`;
}

function formatClock(ms: number) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatResolution(width?: number | null, height?: number | null) {
  if (!width || !height) return "";
  return `${width}x${height}`;
}

function formatFrameRate(value?: number | null) {
  if (!value || value <= 0) return "";
  return `${value.toFixed(value >= 30 ? 0 : 2)} fps`;
}

function formatChannels(value?: number | null) {
  if (!value || value <= 0) return "";
  return `${value}ch`;
}

function compareSessionsByUpdatedAt(left: AdminPlaybackSession, right: AdminPlaybackSession) {
  const leftTime = new Date(left.attributes.updated_at).getTime();
  const rightTime = new Date(right.attributes.updated_at).getTime();
  if (Number.isNaN(leftTime) && Number.isNaN(rightTime)) {
    return left.id.localeCompare(right.id);
  }
  if (Number.isNaN(leftTime)) {
    return 1;
  }
  if (Number.isNaN(rightTime)) {
    return -1;
  }
  return rightTime - leftTime;
}

function isTranscoding(attrs: AdminPlaybackSession["attributes"]) {
  return attrs.delivery_type === "transcode" || attrs.transcode_state.includes("transcode");
}

function playbackProgress(positionMS: number, durationMS?: number | null) {
  if (!durationMS || durationMS <= 0) {
    return 0;
  }
  return clampPercent((positionMS / durationMS) * 100);
}

function clampPercent(value: number) {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.min(100, value));
}

function summarizeUserActivity(sessions: AdminPlaybackSession[]): UserActivitySummary[] {
  const byUser = new Map<string, UserActivitySummary>();
  for (const session of sessions) {
    const attrs = session.attributes;
    const userID = session.relationships.user.data.id;
    const userKey = attrs.username ?? userID;
    const existing = byUser.get(userKey) ?? {
      userKey,
      displayName: attrs.user_display_name ?? attrs.username ?? userID,
      username: attrs.username ?? userID,
      sessionCount: 0,
      watchedMS: 0,
      latestAt: attrs.updated_at,
    };
    existing.sessionCount += 1;
    existing.watchedMS += Math.max(0, attrs.position_ms);
    if (new Date(attrs.updated_at).getTime() > new Date(existing.latestAt).getTime()) {
      existing.latestAt = attrs.updated_at;
    }
    byUser.set(userKey, existing);
  }
  return [...byUser.values()].sort((left, right) => {
    const rightTime = new Date(right.latestAt).getTime();
    const leftTime = new Date(left.latestAt).getTime();
    return rightTime - leftTime;
  });
}

function formatDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleTimeString();
}

function formatDateTimeLong(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}
